import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Search, Pen, Trash2, X as IconX, User as UserIcon, UploadCloud } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  orgUsersOf, branchUsersOf, effectiveBranchesOf, findBranch, ROLES,
  addUser, updateUser, deleteUser, getOrgSettings, warehousesOf, findWarehouse, ORG_TYPES, isModuleOn
} from '../../data/store';
import { roles, loadRoles, offeredRoles } from '../../data/accessStore';
import { useSharedStore } from '../../hooks/useSharedStore';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { FilterPopover } from '../../components/FilterPopover';
import { BulkUploadModal } from '../../components/BulkUploadModal';
import { formatName, formatPhone, formatPincode } from '../../utils/formatters';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';

const ROLE_LABEL = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.ORG_ADMIN]: 'Org Admin',
  [ROLES.BRANCH_ADMIN]: 'Branch Admin',
  [ROLES.WAREHOUSE_ADMIN]: 'Warehouse Admin',
  [ROLES.WAREHOUSE_USER]: 'Warehouse User',
  [ROLES.ORG_USER]: 'User',
  'org-user': 'User',
  'user': 'User',
  'member': 'User',
  'Member': 'User',
};

// Scope is derived from which location id is set; the stored role is always
// one of branch-admin / warehouse-admin / org-user.
const scopeOf = (u) => (u.warehouseId ? 'warehouse' : 'branch');

const blank = (org, lockedBranchId) => ({
  id: null,
  orgId: org?.id,
  name: '',
  firstName: '',
  middleName: '',
  lastName: '',
  gender: '',
  dob: '',
  age: '',
  mobileNumber: '',
  altMobileNumber: '',
  email: '',
  phone: '',
  address: '',
  state: '',
  district: '',
  pinCode: '',
  additionalInfo: '',
  // Typed by whoever is adding this person, because nothing is emailed to
  // them any more. Confirmed, because a password nobody can read back and
  // nobody typed twice is a staff member who cannot sign in and an owner who
  // cannot say why.
  password: '',
  confirmPassword: '',
  scope: 'branch',
  branchId: lockedBranchId || '',
  warehouseId: '',
  role: ROLES.ORG_USER,
  // The role row that carries the permissions. `role` above is the old word,
  // kept for screens that still read it.
  roleId: '',
  isServiceProvider: false,
});

function calculateAge(dobStr) {
  if (!dobStr) return '';
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return '';
  const diff = Date.now() - dob.getTime();
  const ageDate = new Date(diff); 
  return Math.abs(ageDate.getUTCFullYear() - 1970).toString();
}


/**
 * The role picker — one control, whatever the scope.
 *
 * There used to be three: two hard-coded pairs (Branch User / Branch Admin,
 * Warehouse User / Warehouse Admin) and, for a shop with no branches, a grey
 * box reading "User" that could not be changed at all. Which pair you got
 * depended on where the user worked, because the role name carried both what
 * they could do and where they did it.
 *
 * Those are two different questions and they now have two different fields.
 * Branch and Warehouse above say where. This says what, from the roles the
 * shop has actually built — so a shop can have a Cashier who works a branch
 * and a Cashier who works the warehouse, and mean the same thing by it.
 */
const RoleField = ({ draft, setDraft, roleOptions, disabled, wide, error }) => {
  const picked = roleOptions.find((r) => r.value === draft.roleId);
  return (
    <label className={`block space-y-1 ${wide ? 'md:col-span-4' : 'md:col-span-2'}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        Role <span className="text-rose-500 ml-1">*</span>
      </span>
      <CustomSelect
        value={draft.roleId || ''}
        onChange={(v) => {
          const next = roleOptions.find((r) => r.value === v);
          // `role` is the old column, still read by screens that have not moved
          // over yet. A built-in role keeps its original word; one the shop
          // invented has none, and plain staff is the honest answer.
          setDraft({ ...draft, roleId: v, role: next?.systemKey || ROLES.ORG_USER });
        }}
        disabled={disabled}
        options={roleOptions}
        placeholder={roleOptions.length ? '— Select —' : 'No roles yet'}
        className={`w-full bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 ${disabled ? 'opacity-60' : ''} ${error ? 'border border-rose-400' : 'border-gray-100'}`}
      />
      {/* What the name actually buys them. A role list reads as a set of job
          titles, and the person granting one should not have to leave the form
          and open Roles & Access to find out which of them can void a bill. */}
      {!error && picked?.description && (
        <p className="text-[11px] text-slate-500">{picked.description}</p>
      )}
      {error && <p className="text-[11px] font-medium text-rose-500">{error}</p>}
    </label>
  );
};

RoleField.propTypes = {
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  roleOptions: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  wide: PropTypes.bool,
  error: PropTypes.string,
};

const UserModal = ({ open, draft, setDraft, onClose, onSubmit, mode, branches, warehouses, branchesEnabled, warehouseEnabled, canManageRoles, accent, showNotification, isService, roleOptions }) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;

  // The Branch/Warehouse radio only applies when an org admin can manage roles
  // and both modules are on. Otherwise we keep the legacy branch-only behavior.
  const scopePickerOn = canManageRoles && branchesEnabled && warehouseEnabled && warehouses.length > 0;
  const isWarehouse = scopePickerOn && draft.scope === 'warehouse';

  const setScope = (scope) => setDraft({
    ...draft,
    scope,
    // Default to appropriate user role for branch and warehouse. Clear the off-scope id either way.
    role: scope === 'warehouse' ? ROLES.WAREHOUSE_USER : ROLES.ORG_USER,
    branchId: scope === 'branch' ? draft.branchId : '',
    warehouseId: scope === 'warehouse' ? draft.warehouseId : '',
  });

  const handleSubmit = () => {
    const err = {};
    const scopePickerOn = canManageRoles && branchesEnabled && warehouseEnabled && warehouses.length > 0;
    const isWarehouse = scopePickerOn && draft.scope === 'warehouse';
    
    if (!draft.firstName || !draft.firstName.trim()) err.firstName = 'Please fill the First Name.';
    if (!draft.lastName || !draft.lastName.trim()) err.lastName = 'Please fill the Last Name.';
    if (!draft.email || !draft.email.trim()) err.email = 'Please fill the Email Address.';
    // A user with no role can sign in and see nothing, which reads as the app
    // being broken rather than as a form that was left incomplete.
    if (roleOptions.length > 0 && !draft.roleId) err.roleId = 'Choose a role.';
    const phonePattern = /^\+?\d{10,14}$/;
    
    if (!draft.mobileNumber || !draft.mobileNumber.trim()) {
      err.mobileNumber = 'Please fill the Mobile Number.';
    } else if (!phonePattern.test(draft.mobileNumber.replace(/[\s-]/g, ''))) {
      err.mobileNumber = 'Please provide a valid 10-digit mobile number.';
    }

    if (draft.altMobileNumber && !phonePattern.test(draft.altMobileNumber.replace(/[\s-]/g, ''))) {
      err.altMobileNumber = 'Please provide a valid 10-digit alt mobile number.';
    }

    // Only when the account is being made. On edit these boxes are not drawn,
    // and a password is changed by the person who owns it with Forgot Password.
    //
    // Required, because nothing is emailed any more: an account saved without
    // one is an account nobody can get into, and the owner finds out when the
    // new cashier is standing at the till.
    if (mode === 'create') {
      const pw = draft.password || '';
      if (!pw) err.password = 'Set a password for this person.';
      else if (pw.length < 6) err.password = 'A password needs at least 6 characters.';
      else if (pw.length > 128) err.password = 'That password is too long.';
      else if (pw !== (draft.confirmPassword || '')) err.confirmPassword = 'The two passwords do not match.';
    }
    if (!draft.address || !draft.address.trim()) err.address = 'Please fill the Address.';
    if (!draft.state || draft.state.trim().length < 2) err.state = 'Please fill a valid State.';
    if (!draft.district || draft.district.trim().length < 2) err.district = 'Please fill a valid District.';
    if (!draft.pinCode || !draft.pinCode.trim()) {
      err.pinCode = 'Please fill the Pincode.';
    } else if (!/^\d{6}$/.test(draft.pinCode.trim())) {
      err.pinCode = 'Please provide a valid 6-digit Pincode.';
    }

    if (isWarehouse) {
      if (!draft.warehouseId) err.warehouseId = 'Please select a Warehouse.';
    } else if (branchesEnabled && !draft.branchId) {
      err.branchId = 'Please select a Branch.';
    }


    if (Object.keys(err).length > 0) {
      setErrors(err);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden flex flex-col"
          style={{ maxHeight: '95vh' }}>
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}><UserIcon size={18} /></div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit user' : 'Invite user'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{branchesEnabled ? 'Assign branch & role' : 'Invite to organization'}</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>

          <div className="px-6 py-5 overflow-y-auto custom-scrollbar grid grid-cols-1 md:grid-cols-4 gap-4">
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                First name <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="text" value={draft.firstName || ''} onChange={(e) => {
                setDraft({ ...draft, firstName: formatName(e.target.value) });
                if (errors.firstName) setErrors({ ...errors, firstName: undefined });
              }} placeholder="e.g. Priya"
                className={`w-full rounded-lg bg-gray-50 border ${errors.firstName ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.firstName && <p className="text-[11px] font-medium text-rose-500">{errors.firstName}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Middle name</span>
              <input type="text" value={draft.middleName || ''} onChange={(e) => setDraft({ ...draft, middleName: formatName(e.target.value) })} placeholder="e.g. R."
                className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:border-[var(--theme-primary)] focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Last name <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="text" value={draft.lastName || ''} onChange={(e) => {
                setDraft({ ...draft, lastName: formatName(e.target.value) });
                if (errors.lastName) setErrors({ ...errors, lastName: undefined });
              }} placeholder="e.g. Nair"
                className={`w-full rounded-lg bg-gray-50 border ${errors.lastName ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.lastName && <p className="text-[11px] font-medium text-rose-500">{errors.lastName}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Gender</span>
              <CustomSelect
                value={draft.gender}
                onChange={(v) => setDraft({ ...draft, gender: v })}
                options={[
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                  { value: 'Other', label: 'Other' },
                ]}
                placeholder="— Select —"
                className="w-full bg-gray-50 border-gray-100 px-3 py-2 text-xs font-medium normal-case tracking-normal"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">DOB</span>
              <CustomDatePickerFixed
                value={draft.dob || ''}
                onChange={(val) => {
                  setDraft({ ...draft, dob: val, age: calculateAge(val) });
                }}
                className="w-full"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Age</span>
              <input type="number" value={draft.age || ''} readOnly placeholder="Auto-calculated"
                className="w-full rounded-lg bg-gray-100 border border-gray-100 text-slate-500 outline-none px-3 py-2 text-xs font-medium cursor-not-allowed" />
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Email Address <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="email" value={draft.email || ''} onChange={(e) => {
                setDraft({ ...draft, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: undefined });
              }} placeholder="name@org.com"
                className={`w-full rounded-lg bg-gray-50 border ${errors.email ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.email && <p className="text-[11px] font-medium text-rose-500">{errors.email}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Mobile Number <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="tel" value={draft.mobileNumber || ''} onChange={(e) => {
                setDraft({ ...draft, mobileNumber: formatPhone(e.target.value) });
                if (errors.mobileNumber) setErrors({ ...errors, mobileNumber: undefined });
              }} placeholder="9999999999"
                className={`w-full rounded-lg bg-gray-50 border ${errors.mobileNumber ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.mobileNumber && <p className="text-[11px] font-medium text-rose-500">{errors.mobileNumber}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Alt Mobile Number</span>
              <input type="tel" value={draft.altMobileNumber || ''} onChange={(e) => {
                setDraft({ ...draft, altMobileNumber: formatPhone(e.target.value) });
                if (errors.altMobileNumber) setErrors({ ...errors, altMobileNumber: undefined });
              }} placeholder="8888888888"
                className={`w-full rounded-lg bg-gray-50 border ${errors.altMobileNumber ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.altMobileNumber && <p className="text-[11px] font-medium text-rose-500">{errors.altMobileNumber}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                State <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="text" value={draft.state || ''} onChange={(e) => {
                setDraft({ ...draft, state: formatName(e.target.value) });
                if (errors.state) setErrors({ ...errors, state: undefined });
              }} placeholder="e.g. Tamil Nadu"
                className={`w-full rounded-lg bg-gray-50 border ${errors.state ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.state && <p className="text-[11px] font-medium text-rose-500">{errors.state}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                District <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="text" value={draft.district || ''} onChange={(e) => {
                setDraft({ ...draft, district: formatName(e.target.value) });
                if (errors.district) setErrors({ ...errors, district: undefined });
              }} placeholder="e.g. Chennai"
                className={`w-full rounded-lg bg-gray-50 border ${errors.district ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.district && <p className="text-[11px] font-medium text-rose-500">{errors.district}</p>}
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Pincode <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="text" value={draft.pinCode || ''} onChange={(e) => {
                setDraft({ ...draft, pinCode: formatPincode(e.target.value) });
                if (errors.pinCode) setErrors({ ...errors, pinCode: undefined });
              }} placeholder="e.g. 605001"
                className={`w-full rounded-lg bg-gray-50 border ${errors.pinCode ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.pinCode && <p className="text-[11px] font-medium text-rose-500">{errors.pinCode}</p>}
            </label>
            <label className="block space-y-1 md:col-span-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Address <span className="text-rose-500 ml-1">*</span>
              </span>
              <input type="text" value={draft.address || ''} onChange={(e) => {
                setDraft({ ...draft, address: e.target.value });
                if (errors.address) setErrors({ ...errors, address: undefined });
              }} placeholder="Street, building, area..."
                className={`w-full rounded-lg bg-gray-50 border ${errors.address ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
              {errors.address && <p className="text-[11px] font-medium text-rose-500">{errors.address}</p>}
            </label>
            <label className="block space-y-1 md:col-span-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Additional Information</span>
              <textarea value={draft.additionalInfo || ''} onChange={(e) => setDraft({ ...draft, additionalInfo: e.target.value })} placeholder="Any notes or remarks..." rows="2"
                className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] resize-none" />
              {errors.additionalInfo && <p className="text-[11px] font-medium text-rose-500">{errors.additionalInfo}</p>}
            </label>
            {scopePickerOn && (
              <div className="block space-y-1 md:col-span-4">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Assign to</span>
                {/* The word alone. A branch icon beside the word "Branch"
                    repeats it in a second alphabet, and there are only two
                    choices here — nobody is scanning for the right one. */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'branch', label: 'Branch' },
                    { key: 'warehouse', label: 'Warehouse' },
                  ].map(({ key, label }) => {
                    const active = draft.scope === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setScope(key)}
                        aria-pressed={active}
                        className="flex items-center justify-center rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                        style={{
                          borderColor: active ? accent : 'var(--theme-tertiary)',
                          background: active ? 'var(--theme-primary-soft)' : '#fff',
                          color: active ? accent : '#64748b',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isWarehouse ? (
              <>
                <label className="block space-y-1 md:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Warehouse <span className="text-rose-500 ml-1">*</span>
                  </span>
                  <CustomSelect
                    value={draft.warehouseId}
                    onChange={(v) => {
                      setDraft({ ...draft, warehouseId: v });
                      if (errors.warehouseId) setErrors({ ...errors, warehouseId: undefined });
                    }}
                    options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                    placeholder="— Select —"
                    className={`w-full bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 ${errors.warehouseId ? 'border border-rose-400' : 'border-gray-100'}`}
                  />
                  {errors.warehouseId && <p className="text-[11px] font-medium text-rose-500">{errors.warehouseId}</p>}
                </label>
                <RoleField draft={draft} setDraft={setDraft} roleOptions={roleOptions} disabled={!canManageRoles} error={errors.roleId} />
              </>
            ) : branchesEnabled ? (
              <>
                <label className="block space-y-1 md:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Branch <span className="text-rose-500 ml-1">*</span>
                  </span>
                  <CustomSelect
                    value={draft.branchId}
                    onChange={(v) => {
                      setDraft({ ...draft, branchId: v });
                      if (errors.branchId) setErrors({ ...errors, branchId: undefined });
                    }}
                    disabled={!canManageRoles}
                    options={branches.map((b) => ({ value: b.id, label: `${b.name}${b.isMain ? ' (Main)' : ''}` }))}
                    placeholder="— Select —"
                    menuPlacement="top"
                    className={`w-full bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 ${!canManageRoles ? 'opacity-60' : ''} ${errors.branchId ? 'border border-rose-400' : 'border-gray-100'}`}
                  />
                  {errors.branchId && <p className="text-[11px] font-medium text-rose-500">{errors.branchId}</p>}
                </label>
                <RoleField draft={draft} setDraft={setDraft} roleOptions={roleOptions} disabled={!canManageRoles} error={errors.roleId} />
              </>
            ) : (
              <RoleField draft={draft} setDraft={setDraft} roleOptions={roleOptions} disabled={!canManageRoles} error={errors.roleId} wide />
            )}
            <label className="block space-y-1 md:col-span-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Status</span>
              <CustomSelect
                value={draft.status || 'active'}
                onChange={(v) => setDraft({ ...draft, status: v })}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
                className="w-full bg-gray-50 border-gray-100 px-3 py-2 text-xs font-medium normal-case tracking-normal"
              />
            </label>
            {mode === 'create' && (
              <>
                <label className="block space-y-1 md:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Password <span className="text-rose-500 ml-1">*</span>
                  </span>
                  <input type="password" autoComplete="new-password" value={draft.password || ''} onChange={(e) => {
                    setDraft({ ...draft, password: e.target.value });
                    if (errors.password) setErrors({ ...errors, password: undefined });
                  }} placeholder="At least 6 characters"
                    className={`w-full rounded-lg bg-gray-50 border ${errors.password ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
                  {errors.password && <p className="text-[11px] font-medium text-rose-500">{errors.password}</p>}
                </label>
                <label className="block space-y-1 md:col-span-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Confirm Password <span className="text-rose-500 ml-1">*</span>
                  </span>
                  <input type="password" autoComplete="new-password" value={draft.confirmPassword || ''} onChange={(e) => {
                    setDraft({ ...draft, confirmPassword: e.target.value });
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                  }} placeholder="Type it again"
                    className={`w-full rounded-lg bg-gray-50 border ${errors.confirmPassword ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
                  {errors.confirmPassword && <p className="text-[11px] font-medium text-rose-500">{errors.confirmPassword}</p>}
                </label>
              </>
            )}

          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
              style={{ background: accent }}>
              {mode === 'edit' ? 'Save user' : 'Send invite'}
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

UserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  branches: PropTypes.array.isRequired,
  warehouses: PropTypes.array.isRequired,
  branchesEnabled: PropTypes.bool.isRequired,
  warehouseEnabled: PropTypes.bool.isRequired,
  canManageRoles: PropTypes.bool,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
  isService: PropTypes.bool,
  roleOptions: PropTypes.array,
};

export const OrgUsersList = ({ org, branchId, showNotification}) => {
  const { version: dataVersion } = useSharedStore();

  // The shop's roles, for the Role dropdown. Fetched here rather than with the
  // rest of the tenant data because this is the only screen that assigns one,
  // and every till would otherwise pay for a list it never shows.
  useEffect(() => { loadRoles(); }, []);

  // Only the roles the shop is offered, by the same rule Roles & Access lists
  // them under. This used to map every seeded row, so the dropdown offered
  // Warehouse Admin to a shop with no warehouse and a Counter Staff nobody had
  // created — roles that do not appear on the screen that manages them. A role
  // somebody already holds stays in the list whatever the modules say, so that
  // opening their own record does not show an empty Role.
  const roleOptions = useMemo(
    () => {
      const held = new Set(orgUsersOf(org.id).map((u) => u.roleId).filter(Boolean));
      return offeredRoles(org, held).map((r) => ({
        value: r.id, label: r.name, systemKey: r.systemKey, description: r.description || '',
      }));
    },
    [roles.length, dataVersion, org],
  );
  // What to print in the Role column. The assigned role's own name when there
  // is one, so a shop that renamed "Branch Admin" to "Manager" reads Manager;
  // the old fixed label otherwise, for users the seeder has not reached.
  const roleNameOf = (u) => {
    const r = roles.find((x) => x.id === u.roleId);
    return r ? r.name : (ROLE_LABEL[u.role] || u.role);
  };
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ branches: [], warehouses: [], roles: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [draft, setDraft] = useState(blank(org, branchId));
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Org admin manages everyone; a branch admin is locked to their own branch.
  const canManageRoles = !branchId;
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, dataVersion]);
  const warehouses = useMemo(() => warehousesOf(org.id), [org.id, dataVersion]);
  const branchesEnabled = useMemo(() => getOrgSettings(org.id).branchesEnabled, [org.id, dataVersion]);
  const warehouseEnabled = useMemo(() => getOrgSettings(org.id).warehouseEnabled, [org.id, dataVersion]);
  const accent = org.theme.primary;
  
  // Only treat as a service org for STAFF badges if it's purely a service org, 
  // or a BOTH org that has explicitly enabled appointments in their modules.
  const isService = org.type === ORG_TYPES.SERVICE || (org.type === ORG_TYPES.BOTH && org.modules && org.modules.appointments);

  const team = useMemo(() => {
    return orgUsersOf(org.id).filter((u) => {
      if (u.role === ROLES.SUPER_ADMIN) return false;
      if (branchId && u.branchId !== branchId) return false;
      return true;
    });
  }, [org.id, branchId, version, dataVersion]);

  useEffect(() => { setFilters({ branches: [], warehouses: [], roles: [] }); }, [branchId]);

  const filtered = team.filter((u) => {
    let okBranch = true;
    if (filters.branches?.length > 0) okBranch = filters.branches.includes(u.branchId);

    let okWarehouse = true;
    if (filters.warehouses?.length > 0) okWarehouse = filters.warehouses.includes(u.warehouseId);

    let okRole = true;
    if (filters.roles?.length > 0) okRole = filters.roles.includes(u.role);

    const q = search.trim().toLowerCase();
    const okSearch = !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    return okBranch && okWarehouse && okRole && okSearch;
  });

  const showScope = branchesEnabled || warehouseEnabled;

  const openCreate = () => {
    const mainBranchId = branchesEnabled ? (branchId || branches[0]?.id || '') : '';
    setModalMode('create');
    setDraft(blank(org, mainBranchId));
    setModalOpen(true);
  };
  const openEdit = (u) => {
    const scope = scopeOf(u);
    let fName = u.firstName || '';
    let mName = u.middleName || '';
    let lName = u.lastName || '';
    if (!fName && !lName && u.name) {
      const parts = u.name.trim().split(/\s+/);
      fName = parts[0] || '';
      if (parts.length > 2) {
        lName = parts.pop() || '';
        mName = parts.slice(1).join(' ');
      } else if (parts.length === 2) {
        lName = parts[1] || '';
      }
    }
    setDraft({
      ...u,
      firstName: fName,
      middleName: mName,
      lastName: lName,
      mobileNumber: u.mobileNumber || u.phone || '',
      scope,
      warehouseId: u.warehouseId || '',
      branchId: scope === 'warehouse' ? '' : (branchesEnabled ? (u.branchId || branches[0]?.id || '') : ''),
    });
    setModalMode('edit');
    setModalOpen(true);
  };

  const submit = async (record) => {
      const isWarehouse = record.scope === 'warehouse';
      // confirmPassword is for the form to compare and nothing else. The server
      // checks the pair too, and sending it would put the same secret on the
      // wire twice for no reason.
      const { scope, confirmPassword, ...rest } = record;
      const final = {
        ...rest,
        orgId: org.id,
        avatarColor: accent,
        branchId: isWarehouse ? '' : rest.branchId,
        warehouseId: isWarehouse ? rest.warehouseId : '',
      };
      
      if (!final.name && (final.firstName || final.lastName)) {
        final.name = [final.firstName, final.middleName, final.lastName].filter(Boolean).join(' ');
      }

      // Awaited, because what comes back decides what this screen says.
      //
      // It used to announce "Staff member added" and close the form without
      // looking. A duplicate e-mail, a duplicate mobile number, a password the
      // server would not take - every refusal read as success, the row stayed in
      // the list until the next reload, and the first anybody knew of it was the
      // new cashier being told Invalid credentials by the till.
      const result = modalMode === 'create'
        ? await addUser(final)
        : await updateUser(record.id, final);

      if (!result?.ok) {
        if (showNotification) {
          showNotification(result?.error || 'Could not save. Nothing was changed.', 'error');
        }
        // The form stays open with everything still typed into it. Closing it
        // would make somebody fill in eighteen fields again to correct one.
        return;
      }

    if (showNotification) {
      if (modalMode === 'create') {
        // "Invite sent" described an e-mail that is no longer sent. The owner
        // has the password in front of them and has to pass it on themselves,
        // which is the one thing this message should say.
        showNotification('Staff member added. Give them the password you set.', 'success');
      } else {
        showNotification(`User updated.`, 'success');
      }
    }

    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  // The quick promote / demote helper that used to sit here is gone with the
  // button that called it. It flipped `role` between the fixed words and
  // nothing else — which since roles became rows would have moved somebody
  // between two names while leaving their permissions exactly where they were.
  // Changing what a person can do is the Role field in the form now.

  const toggleStatus = async (u) => {
    const nextStatus = u.status === 'inactive' ? 'active' : 'inactive';
    const result = await updateUser(u.id, { status: nextStatus });
    if (showNotification) {
      if (result?.ok) {
        showNotification(`${u.name} is now ${nextStatus === 'inactive' ? 'Inactive' : 'Active'}.`, 'success');
      } else {
        // The toggle has already flipped back - updateUser puts the row as it
        // was. Saying so matters most here: somebody switching an account off
        // is often switching off a person who has left.
        showNotification(result?.error || `Could not change ${u.name}.`, 'error');
      }
    }
    setVersion((v) => v + 1);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const who = confirmDelete.name;
    const result = await deleteUser(confirmDelete.id);
    if (showNotification) {
      if (result?.ok) showNotification(`${who} removed.`, 'success');
      else showNotification(result?.error || `Could not remove ${who}.`, 'error');
    }
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-lg">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors" />
          </div>
          {(branchesEnabled || warehouseEnabled) && (
            <FilterPopover
              accent={accent}
              categories={[
                {
                  id: 'roles',
                  label: 'Roles',
                  options: [
                    ...(branchesEnabled ? [
                      { id: ROLES.BRANCH_ADMIN, label: 'Branch Admin' },
                      { id: ROLES.ORG_USER, label: 'Branch User' }
                    ] : []),
                    ...(warehouseEnabled ? [
                      { id: ROLES.WAREHOUSE_ADMIN, label: 'Warehouse Admin' },
                      { id: ROLES.WAREHOUSE_USER, label: 'Warehouse User' }
                    ] : [])
                  ]
                }
              ]}
              initialFilters={filters}
              onApply={(newFilters) => setFilters(newFilters)}
            />
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button type="button" onClick={() => setBulkUploadOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-slate-500 bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:text-[#0d0d0d] text-xs font-semibold uppercase tracking-[0.14em] transition-colors shadow-sm">
            <UploadCloud size={14} aria-hidden="true" /> Bulk Upload
          </button>
          <button type="button" onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg"
            style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
            <Plus size={14} aria-hidden="true" /> Invite user
          </button>
        </div>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-[var(--theme-tertiary)]">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Mobile Number</th>
                {showScope && <th className="px-3 py-3">Assigned Location</th>}
                <th className="px-3 py-3">Role</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)]">
              {filtered.map((u) => {
                const isWarehouse = scopeOf(u) === 'warehouse';
                const locationName = isWarehouse ? findWarehouse(u.warehouseId)?.name : findBranch(u.branchId)?.name;
                const isAdmin = u.role === ROLES.ORG_ADMIN || u.role === ROLES.BRANCH_ADMIN || u.role === ROLES.WAREHOUSE_ADMIN;
                return (
                  <tr key={u.id} className="hover:bg-slate-50 group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="text-[13px] font-semibold text-[#0d0d0d]">{u.name}</span>
                        {isService && u.isServiceProvider && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] text-white" style={{ background: accent }}>
                            ✂ Staff
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-[12px] text-slate-600">{u.email}</td>
                    <td className="px-3 py-3 text-[12px] text-slate-600">{u.mobileNumber || u.phone || '—'}</td>
                    {showScope && (
                      <td className="px-3 py-3 text-[12px] text-slate-600">
                        {locationName ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{isWarehouse ? 'WH' : 'Branch'}</span>
                            {locationName}
                          </span>
                        ) : '—'}
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.12em]"
                        style={isAdmin ? { color: accent } : { color: '#64748b' }}>
                        {roleNameOf(u)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <StatusToggle
                        on={u.status !== 'inactive'}
                        label={u.name}
                        disabled={!canManageRoles}
                        onChange={() => toggleStatus(u)}
                        title={canManageRoles ? undefined : `${u.name} is ${u.status === 'inactive' ? 'inactive' : 'active'}. You cannot change this.`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <RowActions>
                        <IconAction icon={Pen} label={`Edit ${u.name}`} title="Edit" onClick={() => openEdit(u)} />
                        <IconAction icon={Trash2} tone="danger" label={`Remove ${u.name}`} title="Remove" onClick={() => setConfirmDelete(u)} />
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={showScope ? 6 : 5} className="px-5 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">Their sign-in access will be revoked immediately.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}

      <UserModal
        open={modalOpen}
        draft={draft}
        setDraft={setDraft}
        onClose={() => setModalOpen(false)}
        onSubmit={submit}
        mode={modalMode}
        branches={branches}
        warehouses={warehouses}
        branchesEnabled={branchesEnabled}
        warehouseEnabled={warehouseEnabled}
        canManageRoles={canManageRoles}
        accent={accent}
        showNotification={showNotification}
        isService={org.type === 'service' || org.type === 'both'}
        roleOptions={roleOptions}
      />

      <BulkUploadModal
        isOpen={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={() => setVersion((v) => v + 1)}
        moduleName="Users"
        apiBasePath="/users"
        showNotification={showNotification}
      />
    </div>
  );
};

OrgUsersList.propTypes = {
  org: PropTypes.object.isRequired,
  branchId: PropTypes.string,
  showNotification: PropTypes.func,
  dataVersion: PropTypes.number,
};
